import React from "react";
import Modal from "react-bootstrap/esm/Modal";
import SecondaryButton from "shared/components/SecondaryButton";
import PrimaryButton from "shared/components/PrimaryButton";
import "./EditProfileModal.css"
import { AppState } from '../../../redux/store/store';
import { connect, ConnectedProps } from 'react-redux';
import { toggleAPIRequestSuccess, toggleLoading, getLoggedInAccountInfo, updateCustomerAccount } from '../../../redux/actions/system/actions';
import CustomerAccount from "models/account/customer/customerAccount";
import { isValidPhoneNumber } from "react-phone-number-input";
import { isAfter, isBefore, subYears } from "date-fns";
import InputValidators from "utilities/InputValidators";
import CustomerProfileForm from "components/profile/CustomerProfileForm";

const mapState = (state: AppState) => {
  const { system } = state;
  const { isLoading, apiRequestSuccess, loggedInAccount, subdomain } = system;
  return {
    isLoading,
    apiRequestSuccess,
    loggedInAccount,
    subdomain
  };
};

const mapDispatch = {
  toggleLoading: (isLoading: boolean) => toggleLoading(isLoading),
  toggleAPIRequestSuccess: (apiRequestSuccess: boolean) => toggleAPIRequestSuccess(apiRequestSuccess),
  getLoggedInEmployeeInfo: () => getLoggedInAccountInfo(),
  updateCustomerAccount: (customerAccount: CustomerAccount) => updateCustomerAccount(customerAccount),

}

type callback = () => void;
type newUserCallback = (CustomerAccount: CustomerAccount) => void;
type UserAction = 'create' | 'edit';

interface EditProfileModalProps {
  showModal: boolean;
  action: UserAction;
  onModalCancel: callback;
  newUserCallback?: newUserCallback;

}

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & EditProfileModalProps;

interface EditProfileModalState {
  customerAccount: CustomerAccount;
  validPhoneNumber: boolean;
  validEmail: boolean;
}

class EditProfileModal extends React.Component<Props, EditProfileModalState> {
  currentYear: number = 0;

  constructor(props: any) {
    super(props);

    this.currentYear = new Date().getFullYear();

    this.state = {
      customerAccount: this.props.loggedInAccount as CustomerAccount,
      validPhoneNumber: isValidPhoneNumber(`+1${this.props.loggedInAccount.phoneNumber?.toString()}`),
      validEmail: InputValidators.IsValidEmailAddress(this.props.loggedInAccount.email)
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<EditProfileModalState>, snapshot?: any): void {
    if (prevProps.loggedInAccount !== this.props.loggedInAccount) {
      let validPhoneNumber = isValidPhoneNumber(`+1${this.props.loggedInAccount.phoneNumber?.toString()}`);
      let validEmail = InputValidators.IsValidEmailAddress(this.props.loggedInAccount.email);

      this.setState({ customerAccount: this.props.loggedInAccount, validPhoneNumber, validEmail });
    }

    if (prevProps.apiRequestSuccess !== this.props.apiRequestSuccess && this.props.apiRequestSuccess) {
      this.props.onModalCancel();
      this.props.toggleAPIRequestSuccess(false);
    }
  }

  loadProfile(): void {
    this.props.toggleLoading(true);
    this.props.getLoggedInEmployeeInfo();
  }

  resetForm(): void {
    this.setState({ customerAccount: {...this.props.loggedInAccount} });
    this.props.onModalCancel();
  }

  setDOBState(value: Date) {
    let customerAccount = { ...this.state.customerAccount };
    customerAccount.dateOfBirth = value;
    this.setState({ customerAccount: customerAccount });
  }

  setPhoneNumberState(value: string, data: any, event: any, formattedValue: string): void {
    let customerAccount: CustomerAccount = { ...this.state.customerAccount as CustomerAccount };
    let validNumber = false;

    validNumber = isValidPhoneNumber(`+1 ${formattedValue}`);
    customerAccount.phoneNumber = value;

    this.setState({ customerAccount: customerAccount, validPhoneNumber: validNumber });
  }

  setYearFarmingState(year: number): void {
    let employeeInfo: CustomerAccount = { ...this.state.customerAccount as CustomerAccount };
    employeeInfo.farmingStartYear = year;
    this.setState({ customerAccount: employeeInfo });
  }

  updateProfile(): void {
    if (this.validateData()) {
      this.props.toggleLoading(true);
      this.props.updateCustomerAccount(this.state.customerAccount as CustomerAccount);
    }
  }

  validateData(): boolean {
    let validFirstName: boolean = true, validLastName: boolean = true, validDOB: boolean = true,
      validAddress: boolean = true, validCity: boolean = true, validState: boolean = true, validZipCode: boolean = true,
      validPhoneNumber: boolean = true, validEmail: boolean = true, validFarmingStart: boolean = true;

    validFirstName = this.state.customerAccount?.firstName != undefined && this.state.customerAccount.firstName.length > 0;
    validLastName = this.state.customerAccount?.lastName != undefined && this.state.customerAccount.lastName.length > 0;

    validDOB = this.state.customerAccount !== undefined
      && (this.state.customerAccount.dateOfBirth !== undefined && this.state.customerAccount.dateOfBirth.toString().length > 0)
      && !isBefore(new Date(this.state.customerAccount.dateOfBirth), subYears(new Date(), 125))
      && !isAfter(new Date(this.state.customerAccount.dateOfBirth), new Date());

    validAddress = this.state.customerAccount !== undefined
      && (this.state.customerAccount.streetAddress !== undefined && this.state.customerAccount.streetAddress.length > 0);

    validCity = this.state.customerAccount !== undefined
      && (this.state.customerAccount.city !== undefined && this.state.customerAccount.city.length > 0);

    validState = this.state.customerAccount !== undefined
      && (this.state.customerAccount.state !== undefined && this.state.customerAccount.state !== null && this.state.customerAccount.state.length > 0);

    validZipCode = this.state.customerAccount?.zipCode != undefined && this.state.customerAccount.zipCode.length > 0;

    validPhoneNumber = this.state.validPhoneNumber && this.state.customerAccount?.phoneNumber != undefined && this.state.customerAccount.phoneNumber.toString().length > 0;

    validEmail = this.state.validEmail && this.state.customerAccount?.email != undefined && this.state.customerAccount.email.length > 0;

    validFarmingStart = this.state.customerAccount?.farmingStartYear !== undefined && this.state.customerAccount?.farmingStartYear > 0 && +this.state.customerAccount?.farmingStartYear >= (this.currentYear - 100) && +this.state.customerAccount?.farmingStartYear <= this.currentYear;

    return validFirstName && validLastName && validDOB
      && validAddress && validCity && validState && validZipCode
      && validPhoneNumber && validEmail
      && validFarmingStart;
  }

  render() {
    return (
      <Modal show={this.props.showModal} onHide={this.resetForm.bind(this)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{`${this.props.action === 'create' ? 'Create Profile' : 'Update Profile'}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomerProfileForm
            mode="edit"
            email={this.state.customerAccount?.email}
            firstName={this.state.customerAccount?.firstName}
            lastName={this.state.customerAccount?.lastName}
            suffix={this.state.customerAccount?.suffix}
            dateOfBirth={this.state.customerAccount?.dateOfBirth != undefined ? new Date(this.state.customerAccount.dateOfBirth) : undefined}
            businessName={this.state.customerAccount?.businessName}
            streetAddress={this.state.customerAccount?.streetAddress}
            streetAddress2={this.state.customerAccount?.streetAddress2}
            city={this.state.customerAccount?.city}
            state={this.state.customerAccount?.state}
            zipCode={this.state.customerAccount?.zipCode}
            phoneNumber={this.state.customerAccount?.phoneNumber?.toString()}
            farmingStartYear={this.state.customerAccount?.farmingStartYear}
            onEmailChange={(e) => {
              let account = { ...this.state.customerAccount };
              account.email = e.target.value;
              const validEmail = InputValidators.IsValidEmailAddress(e.target.value);
              this.setState({ customerAccount: account, validEmail });
            }}
            onFirstNameChange={(e) => {
              let account = { ...this.state.customerAccount };
              account.firstName = e.target.value;
              this.setState({ customerAccount: account });
            }}
            onLastNameChange={(e) => {
              let account = { ...this.state.customerAccount };
              account.lastName = e.target.value;
              this.setState({ customerAccount: account });
            }}
            onSuffixChange={(e) => {
              let account = { ...this.state.customerAccount };
              account.suffix = e.target.value;
              this.setState({ customerAccount: account });
            }}
            onDOBChange={this.setDOBState.bind(this)}
            onBusinessNameChange={(e) => {
              let account = { ...this.state.customerAccount };
              account.businessName = e.target.value;
              this.setState({ customerAccount: account });
            }}
            onStreetAddressChange={(e) => {
              let account = { ...this.state.customerAccount };
              account.streetAddress = e.target.value;
              this.setState({ customerAccount: account });
            }}
            onStreetAddress2Change={(e) => {
              let account = { ...this.state.customerAccount };
              account.streetAddress2 = e.target.value;
              this.setState({ customerAccount: account });
            }}
            onCityChange={(e) => {
              let account = { ...this.state.customerAccount };
              account.city = e.target.value;
              this.setState({ customerAccount: account });
            }}
            onStateChange={(e) => {
              let account = { ...this.state.customerAccount };
              account.state = e.target.value;
              this.setState({ customerAccount: account });
            }}
            onZipCodeChange={(e) => {
              let account = { ...this.state.customerAccount };
              account.zipCode = e.target.value;
              this.setState({ customerAccount: account });
            }}
            onPhoneChange={this.setPhoneNumberState.bind(this)}
            onFarmingStartYearChange={(e) => this.setYearFarmingState(+e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <SecondaryButton buttonText={'Cancel'} onClick={this.resetForm.bind(this)} />
          <PrimaryButton
            buttonText={`${this.props.action === 'create' ? 'Create Profile' : 'Update Profile'}`}
            onClick={this.updateProfile.bind(this)}
            disabled={!this.validateData()}
          />
        </Modal.Footer>
      </Modal>
    );
  }
}

export default connector(EditProfileModal);
